import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SearchContextProvider, useSearchContext } from "../../../../../context/searchContext";
import useTabs from "../../../../../hooks/useTabs";
import useI18n from "../../../../../hooks/useTranslations";
import { CustomError } from "../../../../../http/errors";
import { usePatrolSystemsApi } from "../../../../../http/patrol/usePatrolSystems";
import FlexContent from "../../../../ui/FlexContent";
import Header, { HeaderPath } from "../../../../ui/Header";
import Tabs, { Tab } from "../../../../ui/Tabs";
import { PATROL } from "../../AdminSystemSettings";
import Absorbers from "../absorbers/Absorbers";
import AnchorMappings from "../anchorMappings/AnchorMappings";
import Clampings from "../clampings/Clampings";
import CurveIntermediates from "../curveIntermediates/CurveIntermediates";
import SupportExtensions from "../supportExtensions/SupportExtensions";
import SupportExtras from "../supportExtras/SupportExtras";
import SupportOptionals from "../supportOptionals/SupportOptionals";
import Supports from "../supports/Supports";
import SystemMappings from "../systemMappings/SystemMappings";
import Terminals from "../terminals/Terminals";
import PatrolSystemDetails from "./SystemDetails";
import SystemNormatives from "./SystemNormatives";

const PatrolSystem: FC = () => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const systemsApi = usePatrolSystemsApi();

  const aborter = useRef(new AbortController());
  const { error, data, isMutating, trigger } = systemsApi.getAdminPatrolSystem(aborter.current.signal);
  const [state, setState] = useState({
    isAnchorPoint: false,
    isLifeLine: false,
    isClampable: false,
  });

  const path = [
    { url: `/admin/systems`, display: i18n.translation.systems.all },
    { url: `/admin/systems/${systemId}`, display: PATROL },
    {
      url: `/admin/systems/${systemId}/settings?tab=0`,
      display: `${i18n.translation.common.settings}: ${i18n.translation.systems.plural}`,
    },
    { display: data?.name ?? "" },
  ];

  useEffect(() => {
    patrolSystemId && trigger(patrolSystemId);
  }, [patrolSystemId]);

  useEffect(() => {
    data &&
      setState({
        ...data,
      });
  }, [data?.isClampable, data?.isLifeLine, data?.isAnchorPoint]);

  const [searchParams] = useSearchParams();
  const selectedIndex = Number(searchParams.get("tab")) || 0;

  const tabs: Tab[] = [
    { title: i18n.translation.common.generalData },
    { title: i18n.translation.normatives.plural },
    { title: i18n.translation.common.mappings },
    { title: i18n.translation.common.supports, hasSubTabs: true, disabled: !data || !state.isLifeLine },
    { title: i18n.translation.absorbers.plural, disabled: !data || !state.isLifeLine },
    { title: i18n.translation.patrolCurveIntermediates.plural, disabled: !data || !state.isLifeLine },
    { title: i18n.translation.terminals.plural, disabled: !data || !state.isLifeLine },
    { title: i18n.translation.anchorPoints.plural, disabled: !data || !state.isAnchorPoint },
    { title: i18n.translation.clampings.plural, disabled: !data || !state?.isClampable },
  ];

  const contents: ReactNode[] = [
    <FlexContent padding direction="row">
      <PatrolSystemDetails
        patrolSystem={!error ? data : undefined}
        disabled={isMutating || !!error}
        isLoading={!data}
        onUpdate={(isAnchorPoint, isLifeLine, isClampable) =>
          setState({
            isAnchorPoint,
            isLifeLine,
            isClampable,
          })
        }
      />
    </FlexContent>,
    <SystemNormatives active={selectedIndex === 1} />,
    <SystemMappings active={selectedIndex === 2} />,
    <SystemSupports active={selectedIndex === 3} />,
    <Absorbers active={selectedIndex === 4} />,
    <CurveIntermediates active={selectedIndex === 5} />,
    <Terminals active={selectedIndex === 6} />,
    <AnchorMappings active={selectedIndex === 7} />,
    <Clampings active={selectedIndex === 8} />,
  ];

  return (
    <SearchContextProvider>
      <PatrolSystemContent
        title={`${i18n.translation.systems.singular}: ${data?.name ?? ""}`}
        error={error}
        path={path}
        tabs={tabs}
        contents={contents}
      />
    </SearchContextProvider>
  );
};

export default PatrolSystem;

type PatrolSystemContentProps = {
  title: string;
  error?: CustomError;
  path: HeaderPath[];
  tabs: Tab[];
  contents: ReactNode[];
};

const PatrolSystemContent: FC<PatrolSystemContentProps> = ({ title, error, path, tabs, contents }) => {
  const { actions, title: tabTitle } = useSearchContext();

  return (
    <>
      <Header title={tabTitle ? `${title} · ${tabTitle}` : title} error={error} path={path} buttons={actions} />
      <Tabs urlParameter="tab" tabs={tabs} contents={contents} />
    </>
  );
};

type SystemSupportsProps = {
  active?: boolean;
};

const SystemSupports: FC<SystemSupportsProps> = ({ active }) => {
  const i18n = useI18n();

  const controller = useTabs(4, "s_tab");
  const isActiveTab = active ?? true;

  const contents = [
    <Supports active={isActiveTab && controller.selectedIndex === 0} />,
    <SupportExtensions active={isActiveTab && controller.selectedIndex === 1} />,
    <SupportExtras active={isActiveTab && controller.selectedIndex === 2} />,
    <SupportOptionals active={isActiveTab && controller.selectedIndex === 3} />,
  ];

  const tabs: Tab[] = [
    { title: i18n.translation.common.mappings },
    { title: i18n.translation.common.extensions },
    { title: i18n.translation.common.extras },
    { title: i18n.translation.common.optionals },
  ];

  return (
    <Tabs.SubTab>
      <Tabs tabs={tabs} controller={controller} contents={contents} />
    </Tabs.SubTab>
  );
};
